/**
 * Brand identity — ADG (default) or Awagenta, chosen at build time.
 *
 * EventHub is awagenta's event-management platform, shipped white-labelled for
 * ADG Montabaur as the default. Setting `VITE_BRAND=awagenta` flips the
 * customer-facing identity (window/tab title, SuperTokens app name, the header
 * logo in the left menu, and the About dialog's header + licence line) to
 * awagenta's own — the same build-time, env-driven pattern the other
 * deployment facts use (see `utils/mewsConnector.ts`).
 *
 * This is ONLY the product identity. The "developed by awagenta" vendor credit
 * in the menu footer / About dialog is a separate, always-shown attribution and
 * is deliberately NOT part of this switch.
 *
 * Keep this module pure and free of Vue/asset imports so the resolver stays
 * unit-testable; components map `brand.key` to the actual logo asset.
 */
export type BrandKey = 'adg' | 'awagenta'

export interface Brand {
  /** Machine key — components use it to pick the matching logo asset. */
  key: BrandKey
  /** Product title: window/tab title, SuperTokens appName, About header. */
  appTitle: string
  /** Alt text for the header/About logo. */
  logoAlt: string
  /** Attribution / licence line shown in the About dialog; null hides it. */
  licenseLine: string | null
}

const ADG: Brand = {
  key: 'adg',
  appTitle: 'ADG EventHub',
  logoAlt: 'ADG Logo',
  licenseLine:
    'Built using awagenta platform components. ADG Montabaur is granted a perpetual, royalty-free license for internal business operations.',
}

const AWAGENTA: Brand = {
  key: 'awagenta',
  appTitle: 'Awagenta EventHub',
  logoAlt: 'awagenta',
  licenseLine: 'Built on the awagenta event-management platform.',
}

/** Treat an unsubstituted `__VITE_x__` placeholder as absent. */
function isPlaceholder(v: string | undefined): boolean {
  return !!v && v.startsWith('__VITE_') && v.endsWith('__')
}

/**
 * Resolve a brand from the raw env value. Case- and whitespace-insensitive;
 * anything other than `awagenta` (incl. empty, unknown, or an unsubstituted
 * placeholder) falls back to the ADG default.
 */
export function resolveBrand(raw: string | undefined): Brand {
  if (isPlaceholder(raw)) return ADG
  return raw?.trim().toLowerCase() === 'awagenta' ? AWAGENTA : ADG
}

/** The active brand for this build. */
export const brand: Brand = resolveBrand(import.meta.env.VITE_BRAND)
